import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import sumBy from 'lodash/sumBy';
import cloneDeep from 'lodash/cloneDeep';
import useTranslation from '~/hooks/useTranslation';
import roomTypesStyles from '~/styles/bookingFlow/room-types.module.css';
import { FormattedRoomType } from '~/hooks/bookingFlow/useFormattedRoomTypes';
import cnh from '~/helpers/cnh';
import formStyles from '~/styles/form.module.css';
import Alert from '~/components/General/Alert';
import roomTypeResolver from '~/helpers/roomTypeResolver';
import IconBed from '~icons/tabler/bed';

export type RoomTypeValue = FormattedRoomType;

export type RoomTypeReturnedValue = {
  order_session_room_type_id: number;
  service_code: string;
  pax: number;
};

export type RoomTypeInternalValue = Omit<RoomTypeReturnedValue, 'service_code' | 'order_session_room_type_id'> & {
  order_session_room_type_id: number | null;
  service_code: string | null;
};

type Props = {
  value: RoomTypeReturnedValue[];
  roomTypes: RoomTypeValue[];
  pax: number;
  onChange: (value: RoomTypeReturnedValue[]) => void;
  onValidityChange: (valid: boolean) => void;
};

export default function RoomTypes({
  value,
  roomTypes,
  pax,
  onChange,
  onValidityChange,
}: Props) {
  const clonedValue = cloneDeep(value);
  const translate = useTranslation();
  const id = useId();
  const [rows, setRows] = useState<RoomTypeInternalValue[]>(clonedValue || []);

  const selectedPaxCount = useMemo(() => sumBy(rows, 'pax'), [rows]);
  const paxLeft = useMemo(() => pax - selectedPaxCount, [pax, selectedPaxCount]);

  useEffect(() => {
    onValidityChange(selectedPaxCount === pax);
  }, [selectedPaxCount, pax, onValidityChange]);

  useEffect(() => {
    const returnedRows: RoomTypeReturnedValue[] = rows
      .filter((row): row is RoomTypeReturnedValue => row.service_code !== null);

    if (JSON.stringify(clonedValue) !== JSON.stringify(rows)) {
      onChange(returnedRows);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  useEffect(() => {
    setRows(roomTypeResolver(rows, pax));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const onUpdateRoomType = useCallback((
    event: React.ChangeEvent<HTMLSelectElement>,
    index: number,
  ) => {
    const newRows = Array.from(rows);
    const roomType = roomTypes.find((item) => item.service_code === event.target.value);

    newRows[index].service_code = roomType?.service_code ?? null;
    newRows[index].pax = roomType?.pax ?? 0;

    setRows(newRows);
  }, [rows, roomTypes]);

  return (
    <div>
      <h2 className={roomTypesStyles.roomTypesTitle}>
        <IconBed />
        {translate('bookingFlow.overview.room_types')}
      </h2>

      {rows.map((row, index) => {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className={roomTypesStyles.roomTypeRow}>
            <label
              htmlFor={`${id}-${index}`}
              className={roomTypesStyles.roomTypeRowKey}
            >
              {translate('bookingFlow.overview.room_type_number', { number: index + 1 })}
            </label>

            <select
              className={cnh(formStyles.formControl, formStyles.formSelect)}
              id={`${id}-${index}`}
              value={row.service_code ?? ''}
              onChange={(event) => onUpdateRoomType(event, index)}
            >
              <option value="">
                {translate('bookingFlow.overview.room_types_select_a_room_type')}
              </option>

              {roomTypes.map((roomType) => {
                return (
                  <option key={roomType.service_code} value={roomType.service_code}>
                    {roomType.name}
                  </option>
                );
              })}
            </select>
          </div>
        );
      })}

      {paxLeft < 0 && (
        <Alert type="danger" icon="alert-triangle">
          {translate('bookingFlow.overview.room_types_pax_left', { pax, selectedPaxCount })}
        </Alert>
      )}
    </div>
  );
}
